import { cloneDeep } from "lodash";
import { isHttpSuccess } from "utils/functions";
import { showErrorAlert, showSuccessAlert } from "utils/alert";
import { updateAlertNotification } from "service/gatewayService";

type AlertItem = {
    uuid: string;
    source: string;
    said: number | string;
    gatewayUUID: string;
    status: boolean;
    notice?: {
        enabled?: boolean[];
        enabled_push?: boolean;
    };
};

/**
 * Updates the alert enabled/disabled status
 * @param alertUUIDList - Array of alert items
 * @param source - Device source/UID
 * @param said - SAID number
 * @param setAlertUUIDList - State setter for alert list
 * @param setIsAlertLoading - State setter for loading state
 */
export const updateAlertStatus = async (
    alertUUIDList: AlertItem[],
    source: string,
    said: string,
    setAlertUUIDList: (list: AlertItem[]) => void,
    setIsAlertLoading: (value: boolean) => void
) => {
    setIsAlertLoading(true);

    let thisAlertUUID: string | undefined;
    let thisAlertStatus: boolean | undefined;
    let thisGatewayUUID: string | undefined;
    let thisIndex: number | undefined;

    for (let i = 0; i < alertUUIDList.length; i++) {
        if (
            source === alertUUIDList[i].source &&
            said === String(alertUUIDList[i].said)
        ) {
            thisAlertUUID = alertUUIDList[i].uuid;
            thisAlertStatus = alertUUIDList[i].status;
            thisGatewayUUID = alertUUIDList[i].gatewayUUID;
            thisIndex = i;
            break;
        }
    }

    if (!thisAlertUUID || thisAlertStatus === undefined || thisIndex === undefined) {
        setIsAlertLoading(false);
        showErrorAlert({
            message: "Sensor is not yet configured.",
        });
        return;
    }

    const updateResponse: any = await updateAlertNotification(
        thisGatewayUUID || "",
        source,
        said,
        thisAlertUUID,
        { enabled: !thisAlertStatus }
    );

    if (isHttpSuccess(updateResponse.status)) {
        const newAlertList = cloneDeep(alertUUIDList);
        newAlertList[thisIndex].status = !thisAlertStatus;
        setAlertUUIDList(newAlertList);

        showSuccessAlert({
            message: `Alert has been ${
                thisAlertStatus ? "disabled" : "enabled"
            }. It will take up to 2 minutes for the operation to take effect.`,
        });
    } else {
        showErrorAlert({
            title: updateResponse.data.title,
            message: updateResponse.data.description,
        });
    }

    setIsAlertLoading(false);
};

/**
 * Returns the checkbox checked state for a given sensor
 * @param alertUUIDList - Array of alert items
 * @param source - Device source/UID
 * @param said - SAID number
 * @returns boolean indicating if checkbox should be checked
 */
export const returnCheckboxStatus = (
    alertUUIDList: AlertItem[],
    source: string,
    said: number
): boolean => {
    for (const element of alertUUIDList) {
        if (source === element.source && said === element.said) {
            return element.status;
        }
    }
    return false;
};

/**
 * Returns whether the alert toggle should be disabled for a given sensor
 * @param alertUUIDList - Array of alert items
 * @param source - Device source/UID
 * @param said - SAID number
 * @returns boolean indicating if toggle should be disabled
 */
export const returnDisabledStatus = (
    alertUUIDList: AlertItem[],
    source: string,
    said: number
): boolean => {
    for (const element of alertUUIDList) {
        if (source === element.source && said === element.said) {
            if (!element.notice?.enabled?.[0] && !element.notice?.enabled?.[1]) {
                return true;
            }
            return false;
        }
    }
    return true;
};

/**
 * NetworkDetails-specific version that checks alert enabled status
 * Returns the checkbox checked state for a given sensor based on alert's enabled field
 * (matches SensorDetails behavior which only checks data.enabled)
 * @param alertUUIDList - Array of alert items
 * @param source - Device source/UID
 * @param said - SAID number
 * @returns boolean indicating if checkbox should be checked
 */
export const returnCheckboxStatusWithPush = (
    alertUUIDList: AlertItem[],
    source: string,
    said: number
): boolean => {
    if (!source || said === undefined || alertUUIDList.length === 0) {
        return false;
    }
    
    for (const element of alertUUIDList) {
        // Normalize source comparison (trim whitespace, handle null/undefined)
        const normalizedSource = String(source || '').trim();
        const normalizedElementSource = String(element.source || '').trim();
        
        // Compare source and said (handle both string and number types for said)
        if (normalizedSource === normalizedElementSource && String(said) === String(element.said)) {
            // Match SensorDetails behavior: alert is enabled if:
            // 1. The alert's enabled status is true (data.enabled)
            // 2. AND at least one message checkbox is enabled (notice.enabled[0] or notice.enabled[1])
            // This matches how SensorDetails determines if an alert is active
            const isAlertEnabled = element.status === true;
            const hasMessageEnabled = element.notice?.enabled?.[0] === true || element.notice?.enabled?.[1] === true;
            return isAlertEnabled && hasMessageEnabled;
        }
    }
    return false;
};

/**
 * NetworkDetails-specific version that also checks for push notification
 * Returns whether the alert toggle should be disabled for a given sensor
 * @param alertUUIDList - Array of alert items
 * @param source - Device source/UID
 * @param said - SAID number
 * @returns boolean indicating if toggle should be disabled
 */
export const returnDisabledStatusWithPush = (
    alertUUIDList: AlertItem[],
    source: string,
    said: number
): boolean => {
    if (!source || said === undefined || alertUUIDList.length === 0) {
        return true; // Disabled if no alert data available
    }
    
    for (const element of alertUUIDList) {
        // Normalize source comparison (trim whitespace, handle null/undefined)
        const normalizedSource = String(source || '').trim();
        const normalizedElementSource = String(element.source || '').trim();
        
        // Compare source and said (handle both string and number types for said)
        if (normalizedSource === normalizedElementSource && String(said) === String(element.said)) {
            // Alert exists - check if it should be disabled
            // The toggle should be disabled only if:
            // 1. Push notification is explicitly disabled (enabled_push === false)
            // 2. OR no message checkboxes are enabled AND push is not enabled
            // Otherwise, allow toggling (user can configure messages if needed)
            
            const enabledArray = element.notice?.enabled;
            const hasActivationMessage = Array.isArray(enabledArray) && enabledArray[0] === true;
            const hasDeactivationMessage = Array.isArray(enabledArray) && enabledArray[1] === true;
            const hasMessageEnabled = hasActivationMessage || hasDeactivationMessage;
            const pushEnabled = element.notice?.enabled_push === true;
            
            // Disable only if push is explicitly false AND no messages are enabled
            // This allows toggling if either push is enabled OR messages are configured
            if (!pushEnabled && !hasMessageEnabled) {
                return true; // Disabled if both push and messages are not enabled
            }
            
            return false; // Enabled if alert exists and (push is enabled OR messages are configured)
        }
    }
    return true; // Disabled if alert not found in list
};

